import {ActionStatusState} from 'redux/types/utility';
import {BeneficiariesPaperwork, Beneficiary, User} from 'lib/entities';
import {ActionType} from 'redux/actions/beneficiariesActionTypes';
import * as AuthActionTypes from 'redux/actions/authActionTypes';
import {v4 as uuid} from 'uuid';

export const MAX_BENEFICIARIES = 5;

type BeneficiariesPaperworkState = Omit<BeneficiariesPaperwork, 'completedPaperwork'> & {
    completedPaperwork: Record<string, Beneficiary>;
};

export type BeneficiaryState = {
    beneficiary: Beneficiary;
    error?: string | Error;
    isSaved?: boolean;
    isEditing?: boolean;
    isSaving?: boolean;
    isUpdating?: boolean;
    isRemoving?: boolean;
};

export type BeneficiariesState = {
    paperwork?: BeneficiariesPaperworkState;
    beneficiaries: Record<string, BeneficiaryState>;
    saving: ActionStatusState;
    confirming: ActionStatusState;
    starting: ActionStatusState;
    inputValues: Record<string, number>;
};

type Handler = (state: BeneficiariesState, action: Action<any>) => BeneficiariesState;

function mergeBeneficiaryWithState(beneficiaryState: BeneficiaryState, state: BeneficiariesState) {
    if (!beneficiaryState.beneficiary.id) {
        throw new Error('Cannot merge beneficiary without a id');
    }

    return {
        ...state,
        beneficiaries: {
            ...state.beneficiaries,
            [beneficiaryState.beneficiary.id]: {
                ...state.beneficiaries[beneficiaryState.beneficiary.id],
                ...beneficiaryState,
            },
        },
    };
}

function mergePaperworkWithState(paperwork: BeneficiariesPaperwork, state: BeneficiariesState): BeneficiariesState {
    const completedPaperwork: Record<string, Beneficiary> = JSON.parse(paperwork.completedPaperwork || '{}');

    return {
        ...state,
        paperwork: {
            ...paperwork,
            completedPaperwork,
        },
        beneficiaries: {
            ...state.beneficiaries,
            ...Object.values(completedPaperwork).reduce(
                (carry, beneficiary) => ({
                    ...carry,
                    [beneficiary.id]: {
                        ...state.beneficiaries[beneficiary.id],
                        beneficiary,
                        isSaved: true,
                        isEditing:
                            beneficiary.firstName.trim().length === 0 ||
                            beneficiary.payoutPercentage === 0 ||
                            beneficiary.relationship.length === 0,
                    },
                }),
                {},
            ),
        },
    };
}

function removeBeneficiaryFromState(id: string, state: BeneficiariesState): BeneficiariesState {
    const {[id]: removedBeneficiary, ...beneficiaries} = state.beneficiaries;
    return {
        ...state,
        beneficiaries,
    };
}

export const INITIAL_BENEFICIARY_STATE = {
    firstName: '',
    lastName: '',
    relationship: '',
    payoutPercentage: null,
    relationshipNote: '',
    dateOfBirth: '',
} as const;

const ACTION_HANDLERS: Record<ActionType | typeof AuthActionTypes.AUTH_AUTHENTICATE_SUCCESS | string, Handler> = {
    ['beneficiaries/input_values/update']: (state, {payload}: {payload: {id: string; payoutPercentage: number}}) => {
        const {id, payoutPercentage} = payload;
        return {
            ...state,
            inputValues: {
                ...state.inputValues,
                [id]: payoutPercentage,
            },
        };
    },
    [ActionType.BENEFICIARIES_ADD]: (state, {payload}: Action<boolean>): BeneficiariesState =>
        mergeBeneficiaryWithState(
            {
                beneficiary: {
                    id: uuid(),
                    insertedAt: new Date().toISOString(),
                    contingent: payload || false,
                    ...INITIAL_BENEFICIARY_STATE,
                } as any,
                isSaved: false,
                isRemoving: false,
                isUpdating: false,
                isSaving: false,
            },
            state,
        ),
    [ActionType.BENEFICIARIES_CANCEL_ADD]: (state, {payload}: Action<string>): BeneficiariesState =>
        removeBeneficiaryFromState(payload, state),
    [ActionType.BENEFICIARIES_EDIT]: (state, {payload}: Action<string>): BeneficiariesState => {
        const beneficiary = state.beneficiaries[payload]?.beneficiary;

        if (beneficiary) {
            return mergeBeneficiaryWithState(
                {
                    beneficiary,
                    isEditing: true,
                },
                state,
            );
        }

        return state;
    },
    [ActionType.BENEFICIARIES_CANCEL_EDIT]: (state, {payload}: Action<string>): BeneficiariesState => {
        const beneficiary = state.beneficiaries[payload]?.beneficiary;

        if (beneficiary) {
            return mergeBeneficiaryWithState(
                {
                    beneficiary,
                    isEditing: false,
                },
                state,
            );
        }

        return state;
    },
    [ActionType.BENEFICIARIES_SAVE]: (state, {payload}: Action<Beneficiary>): BeneficiariesState =>
        mergeBeneficiaryWithState(
            {
                beneficiary: payload,
                isSaving: true,
            },
            state,
        ),
    [ActionType.BENEFICIARIES_SAVE_SUCCESS]: (
        state,
        {payload, meta: {shouldEndEditing = false}}: Action<Beneficiary>,
    ): BeneficiariesState =>
        mergeBeneficiaryWithState(
            {
                beneficiary: payload,
                isSaving: false,
                isSaved: true,
                ...(shouldEndEditing ? {isEditing: false} : {}),
            },
            state,
        ),
    [ActionType.BENEFICIARIES_SAVE_FAILED]: (state, {error, payload}: Action<Beneficiary>): BeneficiariesState => {
        if (state.beneficiaries[payload.id]) {
            return mergeBeneficiaryWithState(
                {
                    beneficiary: payload,
                    error,
                    isSaving: false,
                },
                state,
            );
        }

        return state;
    },
    [ActionType.BENEFICIARIES_REMOVE]: (state, {payload}: Action<string>): BeneficiariesState => {
        const beneficiary = state.beneficiaries[payload]?.beneficiary;

        if (beneficiary) {
            return mergeBeneficiaryWithState(
                {
                    beneficiary,
                    isRemoving: true,
                },
                state,
            );
        }

        return state;
    },
    [ActionType.BENEFICIARIES_REMOVE_SUCCESS]: (state, {payload}: Action<string>): BeneficiariesState =>
        removeBeneficiaryFromState(payload, state),
    [ActionType.BENEFICIARIES_REMOVE_FAILED]: (state, {error, payload}: Action<string>): BeneficiariesState => {
        const beneficiary = state.beneficiaries[payload]?.beneficiary;

        if (beneficiary) {
            return mergeBeneficiaryWithState(
                {
                    beneficiary,
                    isRemoving: false,
                    error,
                },
                state,
            );
        }

        return state;
    },
    [ActionType.BENEFICIARIES_CONFIRM]: (state): BeneficiariesState => ({
        ...state,
        confirming: {pending: true},
    }),
    [ActionType.BENEFICIARIES_CONFIRM_SUCCESS]: (state): BeneficiariesState => ({
        ...state,
        confirming: {pending: false},
    }),
    [ActionType.BENEFICIARIES_CONFIRM_FAILED]: (state, {error}): BeneficiariesState => ({
        ...state,
        confirming: {pending: false, error},
    }),
    [ActionType.BENEFICIARIES_CANCEL_ALL]: (state): BeneficiariesState => ({
        ...state,
        beneficiaries: Object.entries(state.beneficiaries).reduce((carry, [id, benState]) => {
            if (!benState.isSaved) return carry;

            return {
                ...carry,
                [id]: {
                    ...benState,
                    isEditing: false,
                },
            };
        }, {}),
    }),
    [ActionType.BENEFICIARIES_START]: (state): BeneficiariesState => ({
        ...state,
        starting: {pending: true},
    }),
    [ActionType.BENEFICIARIES_START_FAILED]: (state, {error}): BeneficiariesState => ({
        ...state,
        starting: {pending: false, error},
    }),
    [ActionType.BENEFICIARIES_START_SUCCESS]: (
        state,
        {payload}: Action<BeneficiariesPaperwork>,
    ): BeneficiariesState => ({
        ...mergePaperworkWithState(payload, state),
        starting: {pending: false},
    }),
    [ActionType.BENEFICIARIES_SAVE_PAPERWORK]: (state): BeneficiariesState => ({
        ...state,
        saving: {pending: true},
    }),
    [ActionType.BENEFICIARIES_SAVE_PAPERWORK_FAILED]: (state, {error}): BeneficiariesState => ({
        ...state,
        saving: {pending: false, error},
    }),
    [ActionType.BENEFICIARIES_SAVE_PAPERWORK_SUCCESS]: (state, {payload}): BeneficiariesState => ({
        ...mergePaperworkWithState(payload, state),
        saving: {pending: false},
    }),
    [AuthActionTypes.AUTH_AUTHENTICATE_SUCCESS]: (state, {payload}: Action<User>): BeneficiariesState => {
        const paperwork = payload.paperworks.find(
            (p) => p.formSpec?.label === 'beneficiaries',
        ) as BeneficiariesPaperwork;

        if (paperwork) return mergePaperworkWithState(paperwork, state);

        return state;
    },
};

const initialState = {
    beneficiaries: {},
    saving: {pending: false},
    confirming: {pending: false},
    starting: {pending: false},
    inputValues: {},
};

// eslint-disable-next-line @typescript-eslint/default-param-last
function beneficiariesReducer(state: BeneficiariesState = initialState, action: Action): BeneficiariesState {
    const handler = ACTION_HANDLERS[action.type as ActionType];
    if (handler) return handler(state, action);
    return state;
}

export default beneficiariesReducer;
